import { RefObject, useRef, useState } from 'react';

import { Block } from './types';
import { useToggle } from '../useToggler';

export const useScrollManager = (isPreviewMode: boolean) => {
  const blockLeftRef = useRef<HTMLDivElement | null>(null);
  const blockMiddleRef = useRef<HTMLDivElement | null>(null);
  const blockRightRef = useRef<HTMLDivElement | null>(null);

  const [lastScrolledBlock, setLastScrolledBlock] = useState<Block | null>(null);
  const [isButtonVisible, setIsButtonVisible] = useToggle();

  const blockRefs = new Map<Block, RefObject<HTMLDivElement>>([
    [Block.BlockLeft, blockLeftRef],
    [Block.BlockMiddle, blockMiddleRef],
    [Block.BlockRight, blockRightRef],
  ]);

  const handleScroll = (BlockType: Block) => {
    setLastScrolledBlock(BlockType);
    const blockRef = blockRefs.get(BlockType);
    if (!blockRef?.current) return;
    const { scrollTop, scrollHeight, clientHeight } = blockRef.current;
    const isScrolledPastHalf = scrollTop > (scrollHeight - clientHeight) / 2;

    if (BlockType !== Block.BlockRight || !isPreviewMode) {
      return setIsButtonVisible(isScrolledPastHalf);
    }
    setIsButtonVisible(false);
  };

  const scrollToTop = () => {
    if (lastScrolledBlock === null) return;
    const blockRef = blockRefs.get(lastScrolledBlock);
    if (!blockRef?.current) return;

    blockRefs.forEach((blockRef) => {
      blockRef.current && blockRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    });

    setIsButtonVisible(false);
  };

  const middleBlockScrollToTop = () => {
    if (lastScrolledBlock === null) return;
    const blockRef = blockRefs.get(lastScrolledBlock);
    if (!blockRef?.current) return;

    blockMiddleRef.current && blockMiddleRef.current.scrollTo({ top: 0 });

    setIsButtonVisible(false);
  };

  const leftBlockScrollToBottom = (value: number = 0) => {
    blockLeftRef.current && blockLeftRef.current.scrollTo({ top: value });
  };

  return {
    blockLeftRef,
    blockMiddleRef,
    blockRightRef,
    handleScroll,
    scrollToTop,
    middleBlockScrollToTop,
    leftBlockScrollToBottom,
    isButtonVisible,
  };
};
